import type { LanguageDefinitionResponse } from '@onefootprint/request-types/dashboard';
import type * as monaco from 'monaco-editor';
import type { editor, languages, Position } from 'monaco-editor';

enum TokenType {
  NotOperator = 'NotOperator',
  BinaryLogicalOperator = 'BinaryLogicalOperator',
  IdentifierNamespace = 'IdentifierNamespace',
  Identifier = 'Identifier',
  IncompleteIdentifier = 'IncompleteIdentifier',
  ComparisonOperator = 'ComparisonOperator',
  StartOfArray = 'StartOfArray',
  Comma = 'Comma',
  EndOfExpression = 'EndOfExpression',
  Unknown = 'Unknown',
  StartOfExpression = 'None',
}

export function getCompletionItems(
  monacoInstance: typeof monaco,
  model: editor.ITextModel,
  position: Position,
  languageSpec: LanguageDefinitionResponse,
): languages.ProviderResult<languages.CompletionList> {
  const line = model.getLineContent(position.lineNumber);
  const lineUntilCursor = line.substring(0, position.column - 1);

  const linePrefix = lineUntilCursor.trimEnd();
  const tokensUntilCursor = monacoInstance.editor.tokenize(linePrefix, languageSpec.languageName)[0];

  const lastToken = tokensUntilCursor.length === 0 ? null : tokensUntilCursor[tokensUntilCursor.length - 1];

  const lastTokenStart = lastToken ? lastToken.offset : 0;
  const lastTokenEnd = linePrefix.length;
  const lastTokenVal = linePrefix.substring(lastTokenStart, lastTokenEnd).trim();
  const lastTokenType = tokenType(lastTokenVal, languageSpec);

  const insertAheadRange = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: position.column,
    endColumn: position.column,
  };

  let suggestions = [] as languages.CompletionItem[];

  switch (lastTokenType) {
    case TokenType.StartOfExpression:
    case TokenType.BinaryLogicalOperator:
      // Start a new expression.
      suggestions = [
        ...suggestLhsNamespace(monacoInstance, insertAheadRange, languageSpec),
        ...suggestNot(monacoInstance, insertAheadRange),
      ];
      break;
    case TokenType.NotOperator:
      // Suggest a new expression, but not a double negative.
      suggestions = suggestLhsNamespace(monacoInstance, insertAheadRange, languageSpec);
      break;
    case TokenType.IdentifierNamespace:
    case TokenType.IncompleteIdentifier:
      // Complete the identifier.
      suggestions = suggestLhsFeature(monacoInstance, {
        model,
        cursorPosition: position,
        lastTokenStartOffset: lastTokenStart,
        languageSpec,
      });
      break;
    case TokenType.EndOfExpression:
      // Suggest and/or.
      suggestions = suggestLogicalOperators(monacoInstance, insertAheadRange);
      break;
    case TokenType.ComparisonOperator:
      if (lastTokenVal === 'in') {
        // Suggest start of array.
        suggestions = suggestStartOfArray(monacoInstance, insertAheadRange);
      } else {
        suggestions = suggestRhsValue(monacoInstance, {
          model,
          cursorPosition: position,
          tokensUntilCursor,
          insertAheadRange,
          languageSpec,
        });
      }
      break;

    case TokenType.StartOfArray:
    case TokenType.Comma:
      suggestions = suggestRhsValue(monacoInstance, {
        model,
        cursorPosition: position,
        tokensUntilCursor,
        insertAheadRange,
        languageSpec,
      });
      break;
    case TokenType.Identifier:
      suggestions = suggestComparisonOperators(monacoInstance, lastTokenVal, insertAheadRange, languageSpec);
  }

  // Deduplicate leading space in suggestions or remove it if we're next to an opening bracket.
  const trimLeadingSpace =
    lineUntilCursor.length === 0 ||
    lineUntilCursor.endsWith(' ') ||
    lineUntilCursor.endsWith('[') ||
    lineUntilCursor.endsWith('(');
  if (trimLeadingSpace) {
    suggestions = suggestions.map(s => ({
      ...s,
      insertText: s.insertText.trimStart(),
    }));
  }

  return { suggestions };
}

function tokenType(token: string, languageSpec: LanguageDefinitionResponse): TokenType {
  if (!token) {
    return TokenType.StartOfExpression;
  }

  if (token === 'not') {
    return TokenType.NotOperator;
  }

  if (token === 'and' || token === 'or') {
    return TokenType.BinaryLogicalOperator;
  }

  const operators = Object.values(languageSpec.operatorMap).flat();
  if (operators.includes(token)) {
    return TokenType.ComparisonOperator;
  }

  const identifiers = languageSpec.featureSets.flatMap(fs => fs.features.map(f => `${fs.kind}.${f.name}`));
  if (identifiers.includes(token)) {
    if (token.startsWith('risk_signal.')) {
      // Risk signals don't need an operator and RHS.
      return TokenType.EndOfExpression;
    }

    return TokenType.Identifier;
  }

  const namespaces = languageSpec.featureSets.map(fs => fs.kind);
  if (
    namespaces.find(ns => {
      return token === `${ns}.`;
    })
  ) {
    return TokenType.IdentifierNamespace;
  }

  if (namespaces.find(ns => token.startsWith(`${ns}.`))) {
    return TokenType.IncompleteIdentifier;
  }

  // Checking endsWith because sometimes parens are tokenized together.
  if (token.endsWith(']') || token.endsWith(')')) {
    return TokenType.EndOfExpression;
  }

  if (token === '(') {
    return TokenType.StartOfExpression;
  }

  if (token === '[') {
    return TokenType.StartOfArray;
  }

  if (token === ',') {
    return TokenType.Comma;
  }

  return TokenType.Unknown;
}

function suggestLhsNamespace(
  monacoInstance: typeof monaco,
  range: monaco.IRange,
  languageSpec: LanguageDefinitionResponse,
): languages.CompletionItem[] {
  const namespaces = languageSpec.featureSets.map(fs => fs.kind);

  return namespaces.map(ns => {
    return {
      label: ns,
      kind: monacoInstance.languages.CompletionItemKind.Enum,
      insertText: `${ns}.`,
      range,
    };
  });
}

function suggestLhsFeature(
  monacoInstance: typeof monaco,
  {
    model,
    cursorPosition,
    lastTokenStartOffset,
    languageSpec,
  }: {
    model: editor.ITextModel;
    cursorPosition: Position;
    lastTokenStartOffset: number;
    languageSpec: LanguageDefinitionResponse;
  },
): languages.CompletionItem[] {
  const incompleteIdentifier = model.getValueInRange({
    startLineNumber: cursorPosition.lineNumber,
    endLineNumber: cursorPosition.lineNumber,
    startColumn: lastTokenStartOffset + 1,
    endColumn: cursorPosition.column,
  });

  const namespaces = languageSpec.featureSets.map(fs => fs.kind);
  const namespace = namespaces.find(ns => incompleteIdentifier.startsWith(`${ns}.`));
  if (!namespace) {
    return [];
  }

  const replaceRange = {
    startLineNumber: cursorPosition.lineNumber,
    endLineNumber: cursorPosition.lineNumber,
    // + 1 for the dot, + 1 for 1-based column index
    startColumn: lastTokenStartOffset + namespace.length + 2,
    endColumn: cursorPosition.column,
  };

  const identifiers = languageSpec.featureSets.find(fs => fs.kind === namespace);
  if (!identifiers) {
    return [];
  }

  return identifiers.features.map(feature => {
    return {
      label: feature.name,
      kind: monacoInstance.languages.CompletionItemKind.EnumMember,
      insertText: `${feature.name} `,
      range: replaceRange,

      detail: feature.kind,
      documentation: feature.description,

      // Allow searching by both the actual name and the documentation.
      filterText: `${feature.name} ${feature.description}`,
    };
  });
}

function suggestComparisonOperators(
  monacoInstance: typeof monaco,
  identifier: string,
  range: monaco.IRange,
  languageSpec: LanguageDefinitionResponse,
): languages.CompletionItem[] {
  const namespace = identifier.split('.')[0];
  const fs = languageSpec.featureSets.find(fs => fs.kind === namespace);
  if (!fs) {
    return [];
  }

  const featureName = identifier.split('.').slice(1).join('.');
  const feature = fs.features.find(f => f.name === featureName);
  if (!feature) {
    return [];
  }

  const operators = languageSpec.operatorMap[feature.kind] as string[];

  return operators.map(op => {
    let insertText = ` ${op} `;

    // As a special case, start the array for array operators.
    if (op === 'in' || op === 'not in') {
      // $0 places cursor inside the brackets.
      insertText += '[$0]';
    }

    return {
      label: op,
      kind: monacoInstance.languages.CompletionItemKind.Operator,
      insertText,
      insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      range,
    };
  });
}

function suggestStartOfArray(monacoInstance: typeof monaco, range: monaco.IRange): languages.CompletionItem[] {
  return [
    {
      label: '[]',
      kind: monacoInstance.languages.CompletionItemKind.Operator,
      insertText: '[$0]',
      insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      range,
    },
  ];
}

function suggestNot(monacoInstance: typeof monaco, range: monaco.IRange): languages.CompletionItem[] {
  return [
    {
      label: 'not',
      kind: monacoInstance.languages.CompletionItemKind.Keyword,
      insertText: ' not ',
      range,
    },
  ];
}

function suggestLogicalOperators(monacoInstance: typeof monaco, range: monaco.IRange): languages.CompletionItem[] {
  return [
    {
      label: 'and',
      kind: monacoInstance.languages.CompletionItemKind.Keyword,
      insertText: ' and ',
      range,
    },
    {
      label: 'or',
      kind: monacoInstance.languages.CompletionItemKind.Keyword,
      insertText: ' or ',
      range,
    },
  ];
}

function suggestRhsValue(
  monacoInstance: typeof monaco,
  {
    model,
    cursorPosition,
    tokensUntilCursor,
    insertAheadRange,
    languageSpec,
  }: {
    model: editor.ITextModel;
    cursorPosition: Position;
    tokensUntilCursor: monaco.Token[];
    insertAheadRange: monaco.IRange;
    languageSpec: LanguageDefinitionResponse;
  },
): languages.CompletionItem[] {
  const tokenVals = [];

  for (let i = 0; i < tokensUntilCursor.length; i++) {
    const token = tokensUntilCursor[i];

    const startColumn = token.offset + 1;
    const endColumn = i < tokensUntilCursor.length - 1 ? tokensUntilCursor[i + 1].offset + 1 : cursorPosition.column;
    const range = {
      startLineNumber: cursorPosition.lineNumber,
      endLineNumber: cursorPosition.lineNumber,
      startColumn,
      endColumn,
    };

    const val = model.getValueInRange(range);
    if (!val.trim()) {
      // Filter out whitespace.
      continue;
    }
    tokenVals.push(val.trim());
  }

  const n = tokenVals.length;
  const tokenTypes = tokenVals.map(v => tokenType(v, languageSpec));

  if (n < 2) {
    // Expect at a minimum: [identifier, operator].
    return [];
  }

  // The identifier on the LHS of the operator.
  let identifier: string;
  // Exclude enum values that have already been used in an array RHS.
  let excludeSuggestions: string[] = [];
  if (
    tokenTypes[n - 2] === TokenType.Identifier &&
    tokenTypes[n - 1] === TokenType.ComparisonOperator &&
    tokenVals[n - 1] !== 'in' &&
    tokenVals[n - 1] !== 'not in'
  ) {
    // Suggest a single value.
    identifier = tokenVals[n - 2];
  } else {
    // Suggest a value in an array.
    // Find the last identifier followed by an array comparison operator.
    let identifierIndex = -1;
    for (let i = n - 2; i >= 0; i--) {
      if (
        tokenTypes[i] === TokenType.Identifier &&
        tokenTypes[i + 1] === TokenType.ComparisonOperator &&
        tokenTypes[i + 2] === TokenType.StartOfArray &&
        (tokenVals[i + 1] === 'in' || tokenVals[i + 1] === 'not in')
      ) {
        identifierIndex = i;
        break;
      }
    }

    if (identifierIndex === -1) {
      return [];
    }

    // Exclude tokens after the operator and before the cursor. This list will
    // include some non-suggestable values, like quotes, but that's okay.
    excludeSuggestions = tokenVals.slice(tokenVals.indexOf('[') + 1);

    identifier = tokenVals[identifierIndex];
  }

  const fs = languageSpec.featureSets.find(fs => identifier.startsWith(`${fs.kind}.`));
  const feature = fs?.features.find(f => `${fs.kind}.${f.name}` === identifier);
  if (!feature) {
    return [];
  }

  let suggestions: string[] = [];
  switch (feature.kind) {
    case 'string':
      // $0 places cursor inside the brackets.
      return [
        {
          label: '""',
          kind: monacoInstance.languages.CompletionItemKind.Value,
          insertText: '"$0"',
          insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range: insertAheadRange,
        },
      ];
    case 'enum':
      suggestions = feature.values.filter(v => !excludeSuggestions.includes(v)).map(v => `"${v}"`);
      break;
    case 'boolean':
      suggestions = ['true', 'false'];
      break;
    case 'number':
      // No suggestions for numbers.
      break;
  }

  return suggestions.map(s => ({
    label: s,
    kind: monacoInstance.languages.CompletionItemKind.Value,
    insertText: s,
    range: insertAheadRange,
  }));
}
